import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useAuth, formatApiError } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Car, Loader2 } from 'lucide-react';

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '', country: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try { await register({ ...form, ref_code: refCode }); navigate('/dashboard'); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#0A3A82] flex items-center justify-center">
              <Car className="w-6 h-6 text-[#FADB5F]" />
            </div>
            <span className="font-bold text-2xl text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Euro<span className="text-[#FADB5F]">Moto</span><span className="text-[#FADB5F]/60 text-sm">.Online</span>
            </span>
          </Link>
          <h1 className="text-2xl font-semibold text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('auth.registerTitle')}
          </h1>
        </div>

        <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
          {/* Google Sign Up */}
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 h-11 rounded-lg border border-[#1E293B] bg-[#0B0F19] hover:bg-[#1E293B] text-[#F1F5F9] font-medium text-sm transition-colors mb-4" data-testid="google-register-btn">
            <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-[#1E293B]" />
            <span className="text-xs text-[#475569]">or</span>
            <div className="flex-1 h-px bg-[#1E293B]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-950/40 border border-red-900/50 text-sm text-red-400" data-testid="register-error">
                {error}
              </div>
            )}
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.name')} *</label>
              <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="John Doe" required className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="register-name" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.email')} *</label>
              <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="email@example.com" required className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="register-email" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.password')} *</label>
              <Input type="password" value={form.password} onChange={e => update('password', e.target.value)} placeholder="••••••••" required className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="register-password" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Confirm Password *</label>
              <Input type="password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} placeholder="••••••••" required className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="register-confirm-password" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.phone')}</label>
              <Input value={form.phone} onChange={e => update('phone', e.target.value)} className="bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9]" data-testid="register-phone" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold" data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.registerBtn')}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-[#94A3B8] mt-4">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-[#FADB5F] font-medium hover:underline" data-testid="login-link">
            {t('auth.loginLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
